// src/components/Reviews/Reviews.js
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Avatar,
  CircularProgress,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  InputAdornment,
  IconButton,
  Chip,
  Divider,
  Rating,
  useTheme,
  alpha,
  Tabs,
  Tab,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  ThumbUp,
  ThumbDown,
  Add as AddIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import API from '../config';

export default function Reviews() {
  const { authData } = useContext(AuthContext);
  const token = authData?.access_token;
  const navigate = useNavigate();
  const theme = useTheme();

  // State
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [tabValue, setTabValue] = useState(0);

  // Pending-review modal
  const [pendingOpen, setPendingOpen] = useState(false);
  const [onlyfansUrl, setOnlyfansUrl] = useState('');

  // Fetch reviews
  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    
    setLoading(true);
    axios
      .get(`${API}/api/reviews/user`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((resp) => setReviews(resp.data.reviews || []))
      .catch((err) => {
        console.error(err);
        setError('Failed to load reviews.');
      })
      .finally(() => setLoading(false));
  }, [token, navigate]);

  // Filter reviews
  const filtered = reviews.filter((r) => {
    const term = searchTerm.toLowerCase();
    const text =
      (r.creator_info?.username || '').toLowerCase() + ' ' + (r.fullReview || '').toLowerCase();
    return text.includes(term);
  });

  // Group reviews by recommendation
  const positiveReviews = filtered.filter((r) => r.recommend);
  const negativeReviews = filtered.filter((r) => !r.recommend);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Get reviews based on current tab
  const getDisplayedReviews = () => {
    switch (tabValue) {
      case 0:
        return filtered;
      case 1:
        return positiveReviews;
      case 2:
        return negativeReviews;
      default:
        return filtered;
    }
  };

  // Modal handlers
  const openPending = () => {
    setOnlyfansUrl('');
    setPendingOpen(true);
  };
  
  const closePending = () => setPendingOpen(false);

  const submitPending = async (e) => {
    e.preventDefault();
    if (!onlyfansUrl) {
      toast.error('Please enter a valid OnlyFans URL.');
      return;
    }
    
    try {
      const parts = onlyfansUrl.split('/').filter(Boolean);
      const name = parts.pop();
      await axios.post(
        `${API}/api/reviews/add_pending_review`,
        { url: name },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Your request has been submitted.');
      closePending();
    } catch (err) {
      console.error(err);
      toast.error('Failed to submit your request.');
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '50vh',
        }}
      >
        <CircularProgress size={48} thickness={4} />
        <Typography variant="h6" sx={{ mt: 2, fontWeight: 500 }}>
          Loading your reviews...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert
          severity="error"
          variant="filled"
          sx={{
            borderRadius: 2,
            boxShadow: 2,
          }}
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            textFillColor: 'transparent',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Your Reviews
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          Manage and browse through your creator reviews
        </Typography>
      </Box>

      {/* Search and filters */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 2,
          background: `linear-gradient(to right, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(
            theme.palette.background.paper,
            0.98
          )})`,
          backdropFilter: 'blur(8px)',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <TextField
              fullWidth
              placeholder="Search by creator or review content"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="review tabs"
          centered
          sx={{
            '& .MuiTab-root': {
              fontWeight: 600,
              minWidth: 100,
            },
          }}
        >
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography>All</Typography>
                <Chip 
                  label={filtered.length} 
                  size="small" 
                  sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} 
                />
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ThumbUp fontSize="small" sx={{ mr: 0.5, color: theme.palette.success.main }} />
                <Typography>Positive</Typography>
                <Chip 
                  label={positiveReviews.length} 
                  size="small" 
                  color="success"
                  sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} 
                />
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ThumbDown fontSize="small" sx={{ mr: 0.5, color: theme.palette.error.main }} />
                <Typography>Negative</Typography>
                <Chip 
                  label={negativeReviews.length} 
                  size="small" 
                  color="error"
                  sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} 
                />
              </Box>
            } 
          />
        </Tabs>
      </Box>

      {/* Reviews grid */}
      {getDisplayedReviews().length === 0 ? (
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 2,
            bgcolor: alpha(theme.palette.background.paper, 0.7),
          }}
        >
          <Typography variant="h6" gutterBottom>
            No reviews found
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {searchTerm
              ? 'Try adjusting your search criteria'
              : 'You haven\'t submitted any reviews yet'}
          </Typography>
          {searchTerm && (
            <Button
              variant="outlined"
              onClick={() => setSearchTerm('')}
              sx={{ mt: 1 }}
            >
              Clear Search
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3} sx={{ display: 'flex', justifyContent: 'center' }}>
          {getDisplayedReviews().map((review) => (
            <Grid item xs={12} sm={6} md={4} key={review._id} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Card
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                  // Fixed dimensions for all cards
                  height: 380,
                  width: 320, // Fixed pixel width
                  maxWidth: 320,
                }}
              >
                {/* Creator info header */}
                {review.creator_info && (
                  <CardHeader
                    avatar={
                      <Avatar
                        src={review.creator_info.profilePicture || '/default-profile.png'}
                        alt={review.creator_info.username}
                        sx={{ 
                          width: 40, 
                          height: 40,
                          border: `2px solid ${theme.palette.background.paper}`,
                        }}
                      />
                    }
                    title={
                      <Typography variant="subtitle1" fontWeight={600} noWrap>
                        {review.creator_info.username}
                      </Typography>
                    }
                    action={
                      <Box sx={{ width: 130, textAlign: 'center' }}>
                        <Chip
                          icon={review.recommend ? <ThumbUp fontSize="small" /> : <ThumbDown fontSize="small" />}
                          label={review.recommend ? 'Recommended' : 'Not Recommended'}
                          color={review.recommend ? 'success' : 'error'}
                          size="small"
                          sx={{ mt: 1, width: '100%' }}
                        />
                      </Box>
                    }
                    sx={{ padding: '8px 16px' }}
                  />
                )}
                
                <Divider />
                
                {/* Review content */}
                <CardContent sx={{ flexGrow: 1, pt: 2, width: '100%', padding: '16px' }}>
                  {/* Rating */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                      Quality:
                    </Typography>
                    <Rating
                      value={review.contentQuality}
                      precision={0.5}
                      size="small"
                      readOnly
                    />
                    <Typography variant="body2" fontWeight={500} sx={{ ml: 1 }}>
                      {review.contentQuality}/5
                    </Typography>
                  </Box>
                  
                  {/* Review text */}
                  {review.fullReview && (
                    <Box sx={{ mb: 2, width: 288, maxWidth: 288 }}> {/* 320px card width - 16px padding on each side */}
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Review:
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontStyle: 'italic',
                          color: 'text.primary',
                          lineHeight: 1.6,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          wordBreak: 'break-word',
                          wordWrap: 'break-word',
                          whiteSpace: 'normal',
                          width: 288,
                          maxWidth: 288,
                        }}
                      >
                        "{review.fullReview}"
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Key metrics */}
                  <Grid container spacing={1} sx={{ mt: 1, width: 288, maxWidth: 288 }}>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="caption" color="text.secondary">
                          Spent
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {review.spent || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="caption" color="text.secondary">
                          Frequency
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {review.contentFrequency}/5
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="caption" color="text.secondary">
                          Authenticity
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {review.authenticity}/5
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="caption" color="text.secondary">
                          Engagement
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {review.engagement}/5
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
                
                {/* Footer with date and view button */}
                <Box sx={{ p: 2, pt: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: 288, maxWidth: 288 }}>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: '40%' }}>
                    {new Date(review.createdAt).toLocaleDateString()}
                  </Typography>
                  <Button
                    component={RouterLink}
                    to={`/creator/${review.creator_id}`}
                    size="small"
                    color="primary"
                    sx={{ minWidth: 'auto' }}
                  >
                    View Creator
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Request creator dialog */}
      <Dialog 
        open={pendingOpen} 
        onClose={closePending}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 24,
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>Request New Creator</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 1 }}>
            Enter the OnlyFans URL of the creator you'd like to review
          </Typography>
          <Box component="form" onSubmit={submitPending}>
            <TextField
              label="OnlyFans URL"
              type="url"
              value={onlyfansUrl}
              onChange={(e) => setOnlyfansUrl(e.target.value)}
              placeholder="https://onlyfans.com/creatorname"
              fullWidth
              required
              sx={{ mt: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={closePending} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={submitPending} 
            variant="contained"
            sx={{ px: 3 }}
          >
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
